import {View, Text, StyleSheet} from 'react-native';
import React from 'react';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import I18n, {_m} from 'INTL';

const {fontSemiBold, fontRegular, fontBold, fontExtraBold} = SkillzStyle.fullStyles;

type RTPBannerProps = {
  rtpData: {
    eligible: boolean;
    withdrawal_amount_limit: number;
    daily_frequency_count_limit: number;
    daily_frequency_count: number;
  } | null;
  rtpEnabled: boolean;
  rtpEligible: boolean;
  withdrawalAmount: number;
};

const RTPBanner = ({rtpData, rtpEnabled, rtpEligible, withdrawalAmount}: RTPBannerProps) => {
  const isWithinRTPTransactionLimits =
    rtpData?.daily_frequency_count !== undefined &&
    rtpData?.daily_frequency_count_limit !== undefined &&
    rtpData?.daily_frequency_count < rtpData?.daily_frequency_count_limit;

  const isWithinRTPAmountLimit =
    rtpData?.withdrawal_amount_limit &&
    (Number(withdrawalAmount) || 0) <= rtpData?.withdrawal_amount_limit;

  if (!rtpData || !rtpEnabled || !rtpEligible) {
    return null;
  }

  const rtpAppliedInfo = {
    title: _m('Instant Withdraw - Applied!'),
    info: [
      _m(
        'Limit: {0} - {1} Per day ({2} remaining)',
        I18n.getCurrencyString(rtpData?.withdrawal_amount_limit, '$', false, false),
        rtpData?.daily_frequency_count_limit,
        rtpData?.daily_frequency_count_limit - rtpData?.daily_frequency_count
      ),
    ],
    containerStyle: {
      backgroundColor: '#DAFBD5',
      paddingHorizontal: 8,
      paddingVertical: 16,
      borderRadius: 5,
    },
    titleStyle: {
      color: '#333',
      fontSize: 14,
      ...fontExtraBold,
    },
    infoTextStyle: {
      fontSize: 13,
      ...fontRegular,
      color: '#070707',
    },
    icon: 'icon_withdrawal_instant_unlocked_check.png',
  };

  const amountExceededRTPInfo = {
    title: _m(
      "You're over the {0} instant limit",
      I18n.getCurrencyString(rtpData?.withdrawal_amount_limit, '$', false, true)
    ),
    info: [_m('A fast withdrawal will be sent within 24 hours.*')],
    containerStyle: {
      backgroundColor: '#FEF1BA',
      paddingHorizontal: 8,
      paddingVertical: 16,
      borderRadius: 5,
    },
    titleStyle: {
      fontSize: 14,
      ...fontBold,
      color: '#070707',
    },
    infoTextStyle: {
      fontSize: 13,
      ...fontRegular,
      color: '#757575',
    },
    icon: 'icon_withdrawal_option_instant_warning.png',
  };

  const frequencyExceededRTPInfo = {
    title: _m(
      'Instant limit reached for today ({0} of {1})',
      rtpData?.daily_frequency_count,
      rtpData?.daily_frequency_count_limit
    ),
    info: [_m("You'll still get a fast withdrawal in 24 hours.*")],
    containerStyle: {
      backgroundColor: '#FEF1BA',
      paddingHorizontal: 8,
      paddingVertical: 16,
      borderRadius: 5,
    },
    titleStyle: {
      fontSize: 14,
      ...fontBold,
      color: '#070707',
    },
    infoTextStyle: {
      fontSize: 13,
      ...fontRegular,
      color: '#757575',
    },
    icon: 'icon_withdrawal_option_instant_warning.png',
  };

  const determineRTPBannerInfo = () => {
    if (isWithinRTPTransactionLimits && isWithinRTPAmountLimit) {
      return rtpAppliedInfo;
    }

    if (!isWithinRTPTransactionLimits) {
      return frequencyExceededRTPInfo;
    }

    if (!isWithinRTPAmountLimit) {
      return amountExceededRTPInfo;
    }

    return rtpAppliedInfo;
  };

  const rtpDisplayInfo = determineRTPBannerInfo();

  return (
    <View style={[styles.container, rtpDisplayInfo.containerStyle]}>
      <SkillzImage style={styles.icon} source={rtpDisplayInfo.icon} />
      <View style={styles.banner}>
        <Text style={[styles.title, rtpDisplayInfo.titleStyle]} allowFontScaling={false}>
          {rtpDisplayInfo.title}
        </Text>

        {rtpDisplayInfo.info.map((info, index) => (
          <Text
            key={index}
            style={[styles.infoText, rtpDisplayInfo.infoTextStyle]}
            allowFontScaling={false}
          >
            {info}
          </Text>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 10,
    flexDirection: 'row',
    gap: 5,
  },

  icon: {
    width: 20,
    height: 20,
    marginTop: 5,
  },

  banner: {
    borderRadius: 10,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },

  title: {
    color: '#070707',
    fontSize: 14,
    ...fontSemiBold,
    ...fontExtraBold,
  },

  infoContainer: {
    borderRadius: 10,
  },

  infoText: {
    color: '#070707',
    fontSize: 12,
    ...fontRegular,
  },
});

export default RTPBanner;
